import type { CSSProperties, ReactNode } from 'react';
import type { DetailGroup } from '@/modules/search/searchGrid/utils/searchResultsHelpers';
import { getGtpItemsFromRow, type GeneGtpItem } from '@/utils/formatGtpList';
import { isRelease263Enabled } from '@/config/release263Features';
import { GtpChart } from './GtpChart';

type GtpDetailCardProps = {
  items: GeneGtpItem[];
  className?: string;
  style?: CSSProperties;
};

const CHART_HEIGHT = 22 * 18 + 48;

export function GtpDetailCard({ items, className = '', style }: GtpDetailCardProps) {
  return (
    <section
      className={`min-w-0 overflow-hidden rounded-xl border border-slate-200 pt-0 pb-3 ${className}`}
      style={style}
    >
      <h3 className="mb-2 flex min-h-[2.25rem] items-center justify-between gap-2 rounded-t-xl border-b border-slate-200 bg-gradient-to-r from-pink-50 to-white px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">
        <span className="min-w-0 truncate">• GTAP</span>
        <span
          className="shrink-0 tabular-nums font-light text-gray-500"
          aria-label={`${items.length} MeSH classes`}
          style={{ fontSize: '0.7rem' }}
        >
          {items.length}
        </span>
      </h3>
      <div className="px-1" style={{ height: CHART_HEIGHT }}>
        <GtpChart items={items} />
      </div>
    </section>
  );
}

export function renderDetailGroupsWithGtp({
  groups,
  row,
  renderGroup,
  cardClassName,
  cardStyle,
}: {
  groups: DetailGroup[];
  row: Record<string, unknown> | null | undefined;
  renderGroup: (group: DetailGroup) => ReactNode;
  cardClassName?: string;
  cardStyle?: CSSProperties;
}): ReactNode[] {
  const items = isRelease263Enabled ? getGtpItemsFromRow(row) : [];
  const nodes: ReactNode[] = [];
  let inserted = false;

  groups.forEach(group => {
    nodes.push(renderGroup(group));
    if (group.title === 'Gene' && items.length > 0) {
      nodes.push(
        <GtpDetailCard key="gtp-detail-card" items={items} className={cardClassName} style={cardStyle} />,
      );
      inserted = true;
    }
  });

  if (!inserted && items.length > 0) {
    nodes.push(
      <GtpDetailCard key="gtp-detail-card" items={items} className={cardClassName} style={cardStyle} />,
    );
  }

  return nodes;
}
